import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** Material UI Packages. */
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { customized_style, shipmentMonitoringAutocompleteSx } from 'shared/config/Styles';
import { MainDatagrid } from 'shared/ui/Datagrid';
import ThemeButton from 'shared/ui/Button';
import SocketAlert from 'shared/ui/SocketAlert';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import TabPanel from 'shared/ui/TabPanel';
import { PageHeader } from 'shared/ui/HeaderIcon';
import { readSocketData } from 'shared/lib/fetchSocket';
import { normalizeTeamOptions } from 'shared/lib/teamOptions';

/** Shipment Monitoring Definitions. */
import { StyledPopper, formLabelTypographySx } from 'shared/config/Styles';
import { getShipmentMonitoringColumnsForTab } from './lib/monitoringDefs/index';
import { attachOceanImportQuickSearchFields } from './lib/monitoringDefs/shipmentMonitoringOceanImportQuickSearch';

const SHIPMENT_MONITORING_TAB_LABELS = ['OCEAN IMPORT', 'OCEAN EXPORT', 'AIR IMPORT', 'AIR EXPORT', 'FTL/LTL'];

const ROW_ID_KEYS = [
    'il_ocean_import_id',
    'il_ocean_export_id',
    'il_air_import_id',
    'il_air_export_id',
    'il_ftl_ltl_id',
];

function createEmptyTabPanelState() {
    return {
        filterMode: 'team',
        selectedTeam: null,
        selectedClients: [],
        gridRows: [],
    };
}

/** 토글·Clear·Search·라벨 등(Autocomplete 제외) 공통 높이 */
const FILTER_ROW_MAX_PX = 30;

const TRADE_PARTNER_ROW_FILTER = ['Client', 'Shipper/Consignee', 'Forwarder'];

/** Normalize various `read` payloads into an array of plain objects. */
function unwrapReadArray(data) {
    if (data == null) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.il_trade_partner)) return data.il_trade_partner;
    if (Array.isArray(data.il_shipment_management)) return data.il_shipment_management;
    if (Array.isArray(data.il_team)) return data.il_team;
    const values = Object.values(data);
    if (values.length === 1 && Array.isArray(values[0])) return values[0];
    return [];
}

/** Trade partner options: flat list of `{ id, name }`, deduped by id. */
function normalizeTradePartnerOptions(data) {
    let list = unwrapReadArray(data);
    if (!list.length && data && typeof data === 'object' && !Array.isArray(data)) {
        list = Object.values(data).flatMap((v) => (Array.isArray(v) ? v : []));
    }
    const map = new Map();
    for (const item of list) {
        if (!item || typeof item !== 'object') continue;
        const id = item.id ?? item.il_trade_partner_id;
        const name = item.name ?? item.trade_partner_name ?? '';
        if (id == null) continue;
        if (!map.has(id)) map.set(id, { id, name: String(name) });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeGridRowsForTab(raw, tabValue) {
    const list = unwrapReadArray(raw);
    const idKey = ROW_ID_KEYS[tabValue] || 'id';
    return list.map((row, i) => {
        const base = {
            ...row,
            id: row.id ?? row[idKey] ?? row.shipment_id ?? `shipment-monitoring-${tabValue}-${i}`,
        };
        if (tabValue === 0) {
            return attachOceanImportQuickSearchFields(base);
        }
        return base;
    });
}

export default function ShipmentMonitoring() {
    const dispatch = useDispatch();
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);

    const [tradePartnerOptions, setTradePartnerOptions] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    /** `team` | `client` — 둘 중 하나만 활성, 팀 모드에서는 고객사 직접 선택 불가 */
    const [tabValue, setTabValue] = useState(0);
    const [tabPanels, setTabPanels] = useState(() => ({
        0: createEmptyTabPanelState(),
        1: createEmptyTabPanelState(),
        2: createEmptyTabPanelState(),
        3: createEmptyTabPanelState(),
        4: createEmptyTabPanelState(),
    }));
    const [searchLoading, setSearchLoading] = useState(false);

    const patchTabPanel = useCallback((tabIndex, patch) => {
        setTabPanels((prev) => ({
            ...prev,
            [tabIndex]: { ...prev[tabIndex], ...patch },
        }));
    }, []);

    useEffect(() => {
        document.title = 'Shipment Monitoring | Operation System';
    }, []);

    /** Initial load: clients (`il_trade_partner`) + teams (`il_team`). */
    useEffect(() => {
        if (!socketConnected) return;

        let pending = 2;
        const finishOne = () => {
            pending -= 1;
            if (pending <= 0) setOptionsLoading(false);
        };

        setOptionsLoading(true);
        readSocketData(
            dispatch,
            null,
            'row',
            'il_trade_partner',
            null,
            TRADE_PARTNER_ROW_FILTER,
            (receivedData) => {
                setTradePartnerOptions(normalizeTradePartnerOptions(receivedData));
            },
            () => {},
            finishOne,
        );
        readSocketData(
            dispatch,
            null,
            'row',
            'il_team',
            null,
            null,
            (receivedData) => {
                setTeamOptions(normalizeTeamOptions(unwrapReadArray(receivedData)));
            },
            () => {},
            finishOne,
        );
    }, [dispatch, socketConnected]);

    const handleSearch = useCallback(() => {
        const tabAtSearch = tabValue;
        const panel = tabPanels[tabAtSearch];
        const isTeamMode = panel.filterMode === 'team';
        if (isTeamMode ? !panel.selectedTeam : !panel.selectedClients.length) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: isTeamMode
                        ? 'Select a team before searching.'
                        : 'Select at least one client before searching.',
                    severity: 'warning',
                }),
            );
            return;
        }
        if (!socketConnected) return;

        // 팀으로 찾을 때는 고른 팀 번호만 보낸다. 서버는 화물 행에 적힌 팀(il_team_id)이 그 팀인
        // 건만 돌려준다 — Client Setting의 팀 배정으로 거래처를 풀어 찾던 방식은 더 쓰지 않는다(OS-566).
        const payload = isTeamMode
            ? { team_id: panel.selectedTeam.id, pagination: 1 }
            : { client: panel.selectedClients.map((c) => ({ id: c.id, name: c.name })), pagination: 1 };

        setSearchLoading(true);
        readSocketData(
            dispatch,
            tabAtSearch,
            'row',
            'il_shipment_management',
            null,
            payload,
            (receivedData) => {
                patchTabPanel(tabAtSearch, {
                    gridRows: normalizeGridRowsForTab(receivedData, tabAtSearch),
                });
            },
            () => {},
            () => setSearchLoading(false),
        );
    }, [dispatch, patchTabPanel, socketConnected, tabPanels, tabValue]);

    const handleFilterModeChange = useCallback(
        (_, next) => {
            if (next == null) return;
            patchTabPanel(tabValue, {
                filterMode: next,
                selectedTeam: null,
                selectedClients: [],
            });
        },
        [patchTabPanel, tabValue],
    );

    const handleTeamChange = useCallback(
        (_, team) => {
            patchTabPanel(tabValue, { selectedTeam: team, selectedClients: [] });
        },
        [patchTabPanel, tabValue],
    );

    const handleClearClients = useCallback(() => {
        patchTabPanel(tabValue, { selectedTeam: null, selectedClients: [] });
    }, [patchTabPanel, tabValue]);

    const handleTabChange = useCallback(
        (_, next) => {
            if (next == null || next === tabValue) return;
            setTabValue(next);
        },
        [tabValue],
    );

    const currentPanel = tabPanels[tabValue];

    const columns = useMemo(
        () => getShipmentMonitoringColumnsForTab(tabValue, currentPanel.gridRows),
        [tabValue, currentPanel.gridRows],
    );

    /** Left pin through `feta` when that column exists (ocean tabs). */
    const pinnedLeftColumns = useMemo(() => {
        const targetField = tabValue === 4 ? 'eta' : 'feta';
        const idx = columns.findIndex((c) => c.field === targetField);
        if (idx === -1) return [];
        return columns.slice(0, idx + 1).map((c) => c.field);
    }, [columns, tabValue]);

    const monitoringGridAdditional = useMemo(() => {
        const base = {
            disableColumnReorder: true,
            getRowHeight: () => 'auto',
            pinnedColumns: { left: pinnedLeftColumns, right: [] },
        };
        if (tabValue !== 0) return base;
        return {
            ...base,
            initialState: {
                columns: {
                    columnVisibilityModel: {
                        __quick_search: false,
                    },
                },
                filter: {
                    filterModel: {
                        quickFilterExcludeHiddenColumns: false,
                    },
                },
            },
        };
    }, [pinnedLeftColumns, tabValue]);

    return (
        <>
            <SocketAlert />
            <CustomSnackbar />
            <Paper sx={{ ...customized_style.itemPaper, height: '100vh' }}>
                <PageHeader title="Shipment Monitoring" icon={LocalShippingIcon} sx={{ mb: 0 }} />
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 1,
                        minHeight: 40,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { fontSize: '11px', fontWeight: 600, minHeight: 40, py: 0.5 },
                    }}
                >
                    {SHIPMENT_MONITORING_TAB_LABELS.map((label) => (
                        <Tab key={label} label={label} />
                    ))}
                </Tabs>
                <TabPanel value={tabValue} index={tabValue} boxStyle={{ p: 0, pt: 0.5 }}>
                    <Grid
                        container
                        columnSpacing={1}
                        rowSpacing={0}
                        sx={{
                            mb: 0.5,
                            alignItems: 'center',
                            minHeight: 30,
                        }}
                    >
                        <Grid
                            size={{ xs: 12, md: 7 }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 30,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    width: '100%',
                                    minHeight: 30,
                                    m: 0,
                                    p: 0,
                                }}
                            >
                                <ToggleButtonGroup
                                    exclusive
                                    value={currentPanel.filterMode}
                                    onChange={handleFilterModeChange}
                                    size="small"
                                    color="primary"
                                    sx={{
                                        flexShrink: 0,
                                        alignSelf: 'center',
                                        height: FILTER_ROW_MAX_PX,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        '& .MuiToggleButton-root': {
                                            fontSize: '11px',
                                            py: 0,
                                            px: 1.25,
                                            textTransform: 'none',
                                            lineHeight: 1,
                                            height: '100%',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        },
                                    }}
                                >
                                    <ToggleButton value="team">By team</ToggleButton>
                                    <ToggleButton value="client">By client</ToggleButton>
                                </ToggleButtonGroup>
                                {currentPanel.filterMode === 'team' ? (
                                    <>
                                        <Typography
                                            sx={{
                                                ...formLabelTypographySx,
                                                fontSize: '11px',
                                                lineHeight: 1,
                                                width: 'auto',
                                                minWidth: 44,
                                                minHeight: FILTER_ROW_MAX_PX,
                                                display: 'flex',
                                                alignItems: 'center',
                                                alignSelf: 'center',
                                                m: 0,
                                                p: 0,
                                            }}
                                        >
                                            Team
                                        </Typography>
                                        <Autocomplete
                                            size="small"
                                            options={teamOptions}
                                            value={currentPanel.selectedTeam}
                                            onChange={handleTeamChange}
                                            loading={optionsLoading}
                                            getOptionLabel={(o) => o?.name || ''}
                                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                            slotProps={{
                                                popper: { component: StyledPopper },
                                                listbox: {
                                                    sx: {
                                                        py: 0,
                                                        '& .MuiAutocomplete-option': {
                                                            fontSize: '11px',
                                                            minHeight: 28,
                                                            py: 0.5,
                                                        },
                                                    },
                                                },
                                            }}
                                            sx={shipmentMonitoringAutocompleteSx}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    margin="none"
                                                    label=""
                                                    placeholder={optionsLoading ? 'Loading…' : 'Select team'}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {optionsLoading ? (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={14}
                                                                        sx={{ mr: 0.25 }}
                                                                    />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={{
                                                        m: 0,
                                                        p: 0,
                                                        ...shipmentMonitoringAutocompleteSx,
                                                        '& .MuiInputBase-input::placeholder': {
                                                            fontSize: '11px',
                                                            opacity: 0.65,
                                                        },
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    key={option.id}
                                                    style={{ margin: 0, padding: '4px 8px', fontSize: '11px' }}
                                                >
                                                    {option.name}
                                                </li>
                                            )}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Typography
                                            sx={{
                                                ...formLabelTypographySx,
                                                fontSize: '11px',
                                                lineHeight: 1,
                                                width: 'auto',
                                                minWidth: 44,
                                                minHeight: FILTER_ROW_MAX_PX,
                                                display: 'flex',
                                                alignItems: 'center',
                                                alignSelf: 'center',
                                                m: 0,
                                                p: 0,
                                            }}
                                        >
                                            Client
                                        </Typography>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            limitTags={7}
                                            disableCloseOnSelect
                                            options={tradePartnerOptions}
                                            value={currentPanel.selectedClients}
                                            onChange={(_, v) => patchTabPanel(tabValue, { selectedClients: v })}
                                            loading={optionsLoading}
                                            getOptionLabel={(o) => o?.name || ''}
                                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                            slotProps={{
                                                popper: { component: StyledPopper },
                                                listbox: {
                                                    sx: {
                                                        py: 0.5,
                                                        '& .MuiAutocomplete-option': {
                                                            fontSize: '11px',
                                                            minHeight: 32,
                                                            py: 0.5,
                                                        },
                                                    },
                                                },
                                            }}
                                            sx={{
                                                ...shipmentMonitoringAutocompleteSx,
                                                maxWidth: '75vw',
                                                '& .MuiInputBase-root': {
                                                    flexWrap: 'wrap',
                                                    height: 'auto',
                                                },
                                                '& .MuiInputBase-input': { minHeight: 33, height: 'auto' },
                                            }}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({ index })}
                                                        key={option.id}
                                                        label={option.name}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            maxHeight: 20,
                                                            m: 0,
                                                            mr: '3px',
                                                            '& .MuiChip-label': { fontSize: '10px', px: 0.5, py: 0 },
                                                        }}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    margin="none"
                                                    label=""
                                                    placeholder={optionsLoading ? 'Loading…' : 'Select client(s)'}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {optionsLoading ? (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={14}
                                                                        sx={{ mr: 0.25 }}
                                                                    />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={{
                                                        m: 0,
                                                        p: 0,
                                                        ...shipmentMonitoringAutocompleteSx,
                                                        '& .MuiInputBase-input::placeholder': {
                                                            fontSize: '11px',
                                                            opacity: 0.65,
                                                        },
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    key={option.id}
                                                    style={{ margin: 0, padding: '4px 8px', fontSize: '11px' }}
                                                >
                                                    {option.name}
                                                </li>
                                            )}
                                        />
                                    </>
                                )}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleClearClients}
                                    disabled={!currentPanel.selectedClients.length && !currentPanel.selectedTeam}
                                    startIcon={<ClearIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{
                                        flexShrink: 0,
                                        alignSelf: 'center',
                                        height: FILTER_ROW_MAX_PX,
                                        minHeight: FILTER_ROW_MAX_PX,
                                        maxHeight: FILTER_ROW_MAX_PX,
                                        py: 0,
                                        px: 0.75,
                                        fontSize: '11px',
                                        lineHeight: 1,
                                        color: '#00647e',
                                        borderColor: '#00647e',
                                        textTransform: 'none',
                                        m: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& .MuiButton-startIcon': { mr: 0.25, ml: -0.25 },
                                        '&:hover': { borderColor: '#003a52', bgcolor: 'rgba(0, 100, 126, 0.06)' },
                                    }}
                                >
                                    Clear
                                </Button>
                                <ThemeButton
                                    text="Search"
                                    icon={<SearchIcon />}
                                    width="80px"
                                    onClick={handleSearch}
                                    disabled={!socketConnected || searchLoading || optionsLoading}
                                    sx={{
                                        height: `${FILTER_ROW_MAX_PX}px`,
                                        minHeight: `${FILTER_ROW_MAX_PX}px`,
                                        maxHeight: `${FILTER_ROW_MAX_PX}px`,
                                        alignSelf: 'center',
                                        fontSize: '11px',
                                    }}
                                    iconStyle={{ fontSize: '12px' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ flex: 1, mt: 1, height: '85.5vh', width: '100%', minHeight: 0 }}>
                        <MainDatagrid
                            key={tabValue}
                            rows={currentPanel.gridRows}
                            cols={columns}
                            showToolbar="GridToolbar"
                            loading={searchLoading}
                            id_status={false}
                            additional={{
                                ...monitoringGridAdditional,
                                disableColumnFilter: true,
                                disableRowGrouping: true,
                            }}
                            sx={{
                                height: '100%',
                                width: '100%',
                                fontSize: '10px',
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: '10px',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    fontSize: '10px',
                                    px: 0,
                                },
                                '& .MuiDataGrid-cell': {
                                    fontSize: '10px',
                                    px: 0,
                                    py: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-cell--textCenter': {
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-cell--textLeft': {
                                    justifyContent: 'flex-start',
                                },
                                '& .MuiDataGrid-cell--textRight': {
                                    justifyContent: 'flex-end',
                                },
                                /* 긴 Status는 위에서부터; 나머지 컬럼은 행 높이 기준 세로 중앙 */
                                '& .MuiDataGrid-cell[data-field="status"]': {
                                    alignItems: 'flex-start',
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    fontSize: '10px',
                                },
                                '& .MuiDataGrid-toolbarContainer': {
                                    fontSize: '10px',
                                },
                                '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': {
                                    fontSize: '10px',
                                },
                                '& .MuiDataGrid-panelWrapper': {
                                    fontSize: '10px',
                                },
                                '& .MuiDataGrid-panel': {
                                    fontSize: '10px',
                                },
                            }}
                        />
                    </Box>
                </TabPanel>
            </Paper>
        </>
    );
}
